import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { X, Settings } from 'lucide-react';
import { authAPI } from '../../services/api';

const COOKIE_NAME = 'vett_cookie_consent';
const COOKIE_EXPIRY_DAYS = 365;

// Cookie consent utilities
export const getCookieConsent = () => {
  try {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${COOKIE_NAME}=`));
    if (cookie) {
      return JSON.parse(decodeURIComponent(cookie.split('=')[1]));
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return null;
};

export const setCookieConsent = (consent) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(consent))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  
  // Also store on backend if user is logged in
  const token = localStorage.getItem('vett_token');
  if (token) {
    authAPI.updateCookieConsent({
      essential: consent.essential,
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences
    }).catch(err => console.error('Failed to sync cookie consent to backend:', err));
  }
};

export const hasValidConsent = () => {
  const consent = getCookieConsent();
  if (!consent) return false;
  const consentDate = new Date(consent.timestamp);
  const expiryDate = new Date(consentDate);
  expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
  return new Date() < expiryDate;
};

// Analytics and Marketing script loaders
export const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || measurementId === 'GA_MEASUREMENT_ID') return;
  
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId);
};

export const loadMetaPixel = (pixelId) => {
  if (!pixelId || pixelId === 'PIXEL_ID') return;
  
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};

// Analytics event tracking
export const trackEvent = (eventName, params = {}) => {
  const consent = getCookieConsent();
  if (consent?.analytics && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Meta Pixel event tracking
export const trackMetaEvent = (eventName, params = {}) => {
  const consent = getCookieConsent();
  if (consent?.marketing && window.fbq) {
    window.fbq('track', eventName, params);
  }
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already consented
    if (!hasValidConsent()) {
      setShowBanner(true);
    } else {
      // Load scripts based on existing consent
      const consent = getCookieConsent();
      if (consent?.analytics) {
        loadGoogleAnalytics('GA_MEASUREMENT_ID');
      }
      if (consent?.marketing) {
        loadMetaPixel('PIXEL_ID');
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: new Date().toISOString()
    };
    setCookieConsent(consent);
    loadGoogleAnalytics('GA_MEASUREMENT_ID');
    loadMetaPixel('PIXEL_ID');
    setShowBanner(false);
  };

  const handleRejectNonEssential = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: new Date().toISOString()
    };
    setCookieConsent(consent);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    setCookieConsent(consent);
    
    if (preferences.analytics) {
      loadGoogleAnalytics('GA_MEASUREMENT_ID');
    }
    if (preferences.marketing) {
      loadMetaPixel('PIXEL_ID');
    }
    
    setShowPreferences(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* VDS Cookie Banner - Light Theme */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[100] animate-slide-up"
        style={{ 
          background: '#FFFFFF',
          borderTop: '1px solid #E8EDF5',
          boxShadow: '0 -4px 20px rgba(10, 15, 30, 0.08)',
          padding: '16px 24px',
          fontFamily: "'Bricolage Grotesque', sans-serif"
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1 pr-4">
            <p style={{ 
              color: '#0A0F1E', 
              fontSize: '14px', 
              fontWeight: 600,
              lineHeight: 1.6
            }}>
              We use cookies to improve your experience, analyze traffic, and personalize content. 
              By accepting you consent to our{' '}
              <Link 
                to="/cookies" 
                style={{ color: '#1B5AFF', textDecoration: 'none' }}
                onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.target.style.textDecoration = 'none'}
              >
                Cookie Policy
              </Link>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              onClick={handleAcceptAll}
              style={{ 
                background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)',
                color: '#FFFFFF',
                fontFamily: "'DM Mono', monospace",
                fontSize: '13px',
                fontWeight: 500,
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none'
              }}
              className="w-full sm:w-auto hover:opacity-90 transition-opacity"
              data-testid="cookie-accept-all"
            >
              Accept All
            </Button>
            <Button
              onClick={() => setShowPreferences(true)}
              variant="outline"
              style={{ 
                background: 'transparent',
                color: '#0A0F1E',
                fontFamily: "'DM Mono', monospace",
                fontSize: '13px',
                fontWeight: 500,
                padding: '10px 20px',
                borderRadius: '10px',
                border: '1px solid #E8EDF5'
              }}
              className="w-full sm:w-auto hover:border-[#1B5AFF] hover:bg-[rgba(27,90,255,0.05)] transition-all"
              data-testid="cookie-manage-preferences"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Preferences
            </Button>
            <Button
              onClick={handleRejectNonEssential}
              variant="ghost"
              style={{ 
                background: 'transparent',
                color: '#5B6472',
                fontFamily: "'DM Mono', monospace",
                fontSize: '13px',
                fontWeight: 500,
                padding: '10px 20px',
                borderRadius: '10px'
              }}
              className="w-full sm:w-auto hover:text-[#0A0F1E] transition-colors"
              data-testid="cookie-reject"
            >
              Reject Non-Essential
            </Button>
          </div>
        </div>
      </div>

      {/* VDS Preferences Modal - Light Theme */}
      {showPreferences && (
        <div 
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: 'rgba(10, 15, 30, 0.5)', backdropFilter: 'blur(4px)' }}
        >
          <div 
            style={{ 
              background: '#FFFFFF',
              border: '1px solid #E8EDF5',
              borderRadius: '16px',
              boxShadow: '0 8px 40px rgba(10, 15, 30, 0.15)',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
              overflow: 'hidden',
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
          >
            <div 
              className="flex items-center justify-between p-6"
              style={{ borderBottom: '1px solid #E8EDF5' }}
            >
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: 700, 
                color: '#0A0F1E',
                margin: 0
              }}>
                Cookie Preferences
              </h2>
              <button
                onClick={() => setShowPreferences(false)}
                style={{ 
                  color: '#5B6472',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                className="hover:text-[#0A0F1E] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 160px)' }}>
              {/* Essential */}
              <div 
                className="flex items-start justify-between gap-4 p-4 rounded-xl"
                style={{ background: '#F5F8FF' }}
              >
                <div className="flex-1">
                  <h3 style={{ fontWeight: 700, color: '#0A0F1E', fontSize: '15px', marginBottom: '4px' }}>
                    Essential Cookies
                  </h3>
                  <p style={{ fontSize: '13px', color: '#5B6472', margin: 0, lineHeight: 1.5 }}>
                    Required for the platform to function. Cannot be disabled.
                  </p>
                </div>
                <div className="relative">
                  <div 
                    style={{ 
                      width: '48px', 
                      height: '24px', 
                      background: '#00C9A7',
                      borderRadius: '12px',
                      cursor: 'not-allowed',
                      position: 'relative'
                    }}
                  >
                    <div style={{ 
                      position: 'absolute',
                      right: '4px',
                      top: '4px',
                      width: '16px',
                      height: '16px',
                      background: '#FFFFFF',
                      borderRadius: '50%'
                    }}></div>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div 
                className="flex items-start justify-between gap-4 p-4 rounded-xl"
                style={{ background: '#F5F8FF' }}
              >
                <div className="flex-1">
                  <h3 style={{ fontWeight: 700, color: '#0A0F1E', fontSize: '15px', marginBottom: '4px' }}>
                    Analytics Cookies
                  </h3>
                  <p style={{ fontSize: '13px', color: '#5B6472', margin: 0, lineHeight: 1.5 }}>
                    Helps us understand how users use VETT so we can improve it. Uses Google Analytics.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                  className="relative"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <div style={{ 
                    width: '48px', 
                    height: '24px', 
                    background: preferences.analytics ? '#1B5AFF' : '#E8EDF5',
                    borderRadius: '12px',
                    transition: 'background 0.2s ease',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute',
                      top: '4px',
                      left: preferences.analytics ? '28px' : '4px',
                      width: '16px',
                      height: '16px',
                      background: '#FFFFFF',
                      borderRadius: '50%',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}></div>
                  </div>
                </button>
              </div>

              {/* Marketing */}
              <div 
                className="flex items-start justify-between gap-4 p-4 rounded-xl"
                style={{ background: '#F5F8FF' }}
              >
                <div className="flex-1">
                  <h3 style={{ fontWeight: 700, color: '#0A0F1E', fontSize: '15px', marginBottom: '4px' }}>
                    Marketing Cookies
                  </h3>
                  <p style={{ fontSize: '13px', color: '#5B6472', margin: 0, lineHeight: 1.5 }}>
                    Used to show relevant ads and measure campaign performance. Uses Meta Pixel.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                  className="relative"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <div style={{ 
                    width: '48px', 
                    height: '24px', 
                    background: preferences.marketing ? '#1B5AFF' : '#E8EDF5',
                    borderRadius: '12px',
                    transition: 'background 0.2s ease',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute',
                      top: '4px',
                      left: preferences.marketing ? '28px' : '4px',
                      width: '16px',
                      height: '16px',
                      background: '#FFFFFF',
                      borderRadius: '50%',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}></div>
                  </div>
                </button>
              </div>

              {/* Preferences */}
              <div 
                className="flex items-start justify-between gap-4 p-4 rounded-xl"
                style={{ background: '#F5F8FF' }}
              >
                <div className="flex-1">
                  <h3 style={{ fontWeight: 700, color: '#0A0F1E', fontSize: '15px', marginBottom: '4px' }}>
                    Preference Cookies
                  </h3>
                  <p style={{ fontSize: '13px', color: '#5B6472', margin: 0, lineHeight: 1.5 }}>
                    Remembers your settings and personalizes your experience.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                  className="relative"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <div style={{ 
                    width: '48px', 
                    height: '24px', 
                    background: preferences.preferences ? '#1B5AFF' : '#E8EDF5',
                    borderRadius: '12px',
                    transition: 'background 0.2s ease',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute',
                      top: '4px',
                      left: preferences.preferences ? '28px' : '4px',
                      width: '16px',
                      height: '16px',
                      background: '#FFFFFF',
                      borderRadius: '50%',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}></div>
                  </div>
                </button>
              </div>
            </div>

            <div 
              className="flex gap-3 p-6"
              style={{ borderTop: '1px solid #E8EDF5' }}
            >
              <Button
                onClick={handleSavePreferences}
                style={{ 
                  flex: 1,
                  background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)',
                  color: '#FFFFFF',
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '12px 20px',
                  borderRadius: '10px',
                  border: 'none'
                }}
                className="hover:opacity-90 transition-opacity"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </Button>
              <Button
                onClick={() => setShowPreferences(false)}
                variant="ghost"
                style={{ 
                  color: '#5B6472',
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '12px 20px',
                  borderRadius: '10px'
                }}
                className="hover:text-[#0A0F1E] transition-colors"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}} />
    </>
  );
};

export default CookieConsent;
